import {
  BooleanFilterSection,
  DiscountLevelFilter as DiscountLevelFilterType,
  PriceFilterSection,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/filter_section.types';

import {useEffect, useMemo, useState} from 'react';
import {RANGE_FILTER_DELIMITER} from '../../../shared-deprecated/rangeFilterUtils';

const isValidInt = (value: string | undefined | null) => {
  if (value === undefined || value === null) return false;
  const parsed = parseInt(value, 10);
  return typeof parsed === 'number' && !isNaN(parsed) && isFinite(parsed);
};

const getValidValue = (
  values: string[] | undefined,
  index: number,
): false | number => {
  return values && isValidInt(values[index]) && parseInt(values[index]!)
    ? parseInt(values[index]!)
    : false;
};

export const usePriceFilterSection = ({
  data,
  activeFilters,
  setFilter,
}: {
  data: PriceFilterSection;
  activeFilters: Map<string, Array<string> | undefined>;
  setFilter: (filterData: {
    filterId: string;
    filterSection: string;
    singleSelectOnly?: boolean;
  }) => void;
}) => {
  const {priceSlider, sale} = data;

  const totalMax = priceSlider?.max ?? 0;
  const totalMin = priceSlider?.min ?? 0;
  const slug = priceSlider?.slug;
  const currencyCode = priceSlider.currencyCode;

  const activePrices = useMemo(
    () => (slug ? activeFilters.get(slug) : undefined),
    [activeFilters, slug],
  );

  const activeMinFromFilters = getValidValue(activePrices, 0);
  const activeMin =
    activeMinFromFilters && activeMinFromFilters >= totalMin
      ? activeMinFromFilters
      : totalMin;

  const activeMaxFromFilters = getValidValue(activePrices, 1);
  const activeMax =
    activeMaxFromFilters && activeMaxFromFilters <= totalMax
      ? activeMaxFromFilters
      : totalMax;

  const [values, setValues] = useState<[number, number]>([
    activeMin,
    activeMax,
  ]);

  // activeMax and activeMin are derived from `activePrices`, thus needs to update it in state when they change (in url)
  useEffect(() => {
    // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
    setValues([activeMin, activeMax]);
  }, [activeMax, activeMin]);

  const {
    label: saleFilterLabel,
    slug: saleFilterSlug,
    value: saleFilterValue,
  } = getFilterData({activeFilters, filter: sale});
  const saleFilterActive = Boolean(
    saleFilterValue?.[0] && saleFilterValue[0] === 'true',
  );

  const handleApplyPriceRange = (range: [number, number]) => {
    setFilter({
      filterId: range.join(RANGE_FILTER_DELIMITER),
      filterSection: slug!,
    });
  };

  return {
    priceSlider,
    totalMax,
    totalMin,
    slug,
    setValues,
    values,
    saleFilterLabel,
    saleFilterSlug,
    saleFilterActive,
    handleApplyPriceRange,
    hasAppliedPriceRange: Boolean(activePrices),
    currencyCode,
  };
};

const getFilterData = ({
  activeFilters,
  filter,
}: {
  activeFilters: Map<string, Array<string> | undefined>;
  filter?: DiscountLevelFilterType | BooleanFilterSection;
}) => {
  if (!filter) {
    return {
      active: false,
      label: undefined,
      slug: undefined,
    };
  }

  const label = filter.label;
  const slug = filter.slug;
  const value = activeFilters.get(slug);

  return {
    label,
    slug,
    value,
  };
};
